<template>
  <div :class="searchAreaClass">
    <div class="text-headline-small d-flex justify-content-between mt-0 mb-4">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z" fill-rule="evenodd" sandboxuid="0"></path>
        </template>
        <template v-if="!hide">
          <path d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z" sandboxuid="0" />
        </template>
      </svg>
    </div>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : '' }`">
      <div class="text-body-medium d-grid gap-2">
        <v-select
          class="mb-1"
          v-model="selectedOfficeableType"
          :items="showableOfficeableTypes"
          item-title="name"
          item-value="id"
          v-bind="{'return-object': true}"
          :label="i18n.global.t('pages.accounts.accountCategory')"
          :clearable="false"
        />
        <OrganizationAutoComplate
          class="mb-1"
          v-model="selectedOffice"
          :selectedOfficeableType="selectedOfficeableType"
        />
        <AccountNamesAutoComplete
          v-model="selectedAccountsName"
          class="mb-1"
          :operationOffices="selectedOffice ? [selectedOffice] : []"
        />
        <DepartmentAutoComplete
          v-model="selectedDepartment"
          class="mb-1"
          :selectFirstIfOnlyOne="isDepartment"
          :operationOffices="selectedOffice ? [selectedOffice] : []"
        />
        <v-select
          v-model="roleId"
          class="mb-1"
          :items="ROLES"
          item-title="name"
          item-value="value"
          :label="i18n.global.t('pages.accounts.role')"
        />
        <div class="p-1">
          <div style="font-size: 13px; color: gray; font-weight: bold;">
            {{ i18n.global.t('pages.common.filter.status.title') }}
          </div>
          <v-radio-group
            v-model="status"
            class="radio-group-row"
          >
            <v-radio
              value="only-active"
              :label="i18n.global.t('pages.common.filter.status.onlyActive')"
              color="blue"
            ></v-radio>
            <v-radio
              value="all"
              :label="i18n.global.t('pages.common.filter.status.all')"
              color="blue"
            ></v-radio>
          </v-radio-group>
        </div>
      </div>
      <div class="d-flex flex-column">
        <v-divider
          :thickness="3"
          class="my-2"
        />
        <div class="d-flex search-buttons" >
          <v-btn
            class="ma-2 exec-btn"
            @click="handleClickSearch"
            :style="{
              backgroundColor : loading ? '#B3B8BB' : ''
            }"
          >
            {{ searchBtnLabel }}
          </v-btn>
          <v-btn
            class="ma-2 reset-btn"
            @click="clearForm"
          >
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AccountNamesAutoComplete from "@/components/AutoCompletes/AccountNamesAutoComplete.vue";
import OrganizationAutoComplate from "@/components/AutoCompletes/OrganizationAutoComplate.vue";
import DepartmentAutoComplete from "@/components/AutoCompletes/DepartmentAutoComplete.vue"
import getAccountsResponse from "@/types/requests/getAccounts";
import getDepartmentResponse from '@/types/responses/getDepartment'
import { DEPARTMENT_OFFICE_TYPE } from "@/services/consts"
import { Ref, onMounted, ref, computed, watch } from 'vue';
import Api from "@/services/api/ApiServiceFabrick";
import { ROLES } from "@/services/consts/account";
import i18n from '@/lang/i18n';
import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";

const emits = defineEmits(['search', 'clear'])

const props = defineProps<{
  loading: boolean
}>()

type officeType = {
  id: number | null,
  name: string,
  type: string
}

type selectedOfficeType = {
  id: number | null,
  type: string,
  name: string,
  departments: any[]
}

const MANAGEMENT_OFFICE = {
  id: 1,
  name: i18n.global.t('pages.accounts.managementOffice'),
  type: 'ManagementOffice'
}
const SUB_MANAGEMENT_OFFICE = {
  id: 2,
  name: i18n.global.t('pages.accounts.subManagementOffice'),
  type: 'SubManagementOffice'
}
const INSTALLATION_OFFICE = {
  id: 3,
  name: i18n.global.t('pages.accounts.installationOffice'),
  type: 'InstallationOffice'
}
const OPERATION_OFFICE = {
  id: 4,
  name: i18n.global.t('pages.accounts.operationOffice'),
  type: 'OperationOffice'
}
const selectedOfficeableType: Ref<officeType> = ref({ id: null, name: "", type: "" })
const showableOfficeableTypes: Ref<officeType[]> = ref([])
const selectedOffice: Ref<selectedOfficeType> = ref({ id: null, type: "", name: "", departments: [] })
const selectedAccountsName: Ref<getAccountsResponse | undefined> = ref()
const selectedDepartment: Ref<getDepartmentResponse | undefined> = ref()
const status: Ref<string> = ref('only-active')
const hide: Ref<boolean> = ref(false)
const isDepartment: Ref<boolean> = ref(false)
const roleId: Ref<number | undefined> = ref(undefined)

onMounted(() => {
  Api().getSelfAccount().then((data) => {
    isDepartment.value = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
    const type = data.login_organization_type?.split('\\')[2];
    switch (type) {
      case MANAGEMENT_OFFICE.type:
        showableOfficeableTypes.value = [MANAGEMENT_OFFICE, SUB_MANAGEMENT_OFFICE, INSTALLATION_OFFICE, OPERATION_OFFICE];
        break;
      case SUB_MANAGEMENT_OFFICE.type:
        showableOfficeableTypes.value = [SUB_MANAGEMENT_OFFICE, INSTALLATION_OFFICE, OPERATION_OFFICE];
        break;
      case OPERATION_OFFICE.type:
        showableOfficeableTypes.value = [OPERATION_OFFICE];
        break;
      default:
        showableOfficeableTypes.value = [];
    }
    if (showableOfficeableTypes.value.length) {
      showableOfficeableTypes.value.unshift({ id: null, type: "", name: "" });
    }
    const savedFormValues = loadDataFromSessionStorage('accountSearchForm');
    if (savedFormValues) {
      selectedOfficeableType.value = savedFormValues.officeable_type;
      selectedOffice.value = savedFormValues.selected_office;
      selectedAccountsName.value = savedFormValues.account_name;
      selectedDepartment.value = savedFormValues.department;
      roleId.value = savedFormValues.role_id;
      status.value = savedFormValues.status;
    }
    watch(() => selectedOffice.value, (newValue) => {
      const isIncluded = newValue?.departments.some(department => department.id === selectedDepartment.value?.id)
      if (!isIncluded) {
        selectedDepartment.value = undefined;
      }
    });
  })
})

const title = computed(() => {
  return hide.value ? "" : i18n.global.t('pages.common.filter.title')
})

const searchBtnLabel = computed(() => {
  return props.loading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec')
})

const searchAreaClass = computed(() => {
  return hide.value ? "search-area-hide p-4" : "search-area p-4"
})

function handleClickSearch() {
  const params = {
    officeable_type: selectedOfficeableType.value.id,
    officeable_id: selectedOffice.value?.id,
    type_class: selectedOffice.value?.type ? 'App\\Models\\' + selectedOffice.value.type : null,
    account_name: selectedAccountsName.value?.name,
    department_id: selectedDepartment.value?.id,
    role_id: roleId.value,
    status: status.value
  };
  emits('search', params);
  saveDataToSessionStorage('accountSearchForm', {
    officeable_type: selectedOfficeableType.value,
    selected_office: selectedOffice.value,
    account_name: selectedAccountsName.value,
    department: selectedDepartment.value,
    role_id: roleId.value,
    status: status.value,
  });
}

function clearForm() {
  selectedOfficeableType.value = { id: null, name: "", type: "" }
  selectedOffice.value = { id: null, type: "", name: "", departments: [] }
  selectedAccountsName.value = { id: undefined, name: "", role_id: undefined };
  roleId.value = undefined;
  status.value = 'only-active';
  clearSessionStorage('accountSearchForm');
  emits('clear');
}

function toggleSidebar() {
  hide.value = !hide.value
}
</script>
